import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MferHead } from './MferHead';
import { sanitizeUserName } from './gameLogic';
import GameOverModal from './GameOverModal';
import Timer from './Timer';
import MovesCounter from './MovesCounter';
import PlayerInfo from '../shared/PlayerInfo';
import BackgroundMusic from './BackgroundMusic';
import CONSTANTS from './constants';
import { v4 as uuidv4 } from 'uuid';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import './Board.css';
import { Howl } from 'howler';
import { useGameAudio } from './hooks/useGameAudio';
import { useGameState } from './hooks/useGameState';
import { useBoardState } from './hooks/useBoardState';
import { useReplayRecorder } from './hooks/useReplayRecorder';
import { useJewelInteraction } from './hooks/useJewelInteraction';
import { useAutoplay } from './hooks/useAutoplay';
import { useSwipeInteraction } from './hooks/useSwipeInteraction';
import { useAuthContext } from '../../contexts/AuthContext';
import { GameBoard, StateChange } from '../../types/games/mfersbeheaded';
import { sendTelemetry } from '../../utils/telemetry';

interface LevelConfig {
  AUTOPLAY?: boolean;
  AUTOPLAY_INTERVAL_MS?: number;
  BOARD_SIZE: number;
  LEVEL_NAME: string;
  ID: string;
  BG_AUDIOS?: string[];
  END_AUDIOS?: string[];
  BG_IMAGE?: string;
  BASIC_SOUND?: string;
  ADV_SOUND_CLIPS?: string[];
  TIME_LIMIT: number | null;
  MAX_MOVES: number | null;
  BARRELS_ENABLED?: boolean;
  BARREL_POSITIONS?: [number, number][];
  IMAGE_MAP: { [key: number]: string };
  POWER_UPS_ENABLED?: boolean;
  VERSION?: string;
  IS_TEST?: boolean;
  BOARD_INITIAL_STATE?: number[][] | null;
  CREDITS?: string[];
}

interface BoardProps {
  config: LevelConfig;
  reloadBoardKey: () => void;
}

interface LevelDetails {
  LEVEL_NAME: string;
  LEVEL_ID: string;
  LEVEL_VERSION: string;
  CREDITS: string[];
  TIME_LIMIT: number | null;
  MAX_MOVES: number | null;
}

function Board({ config, reloadBoardKey }: BoardProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Get auth state from new auth system
    const { isAuthenticated, user } = useAuthContext();
    const userId = isAuthenticated && user ? user.id : null;
    const authDisplayName = isAuthenticated && user ? user.displayName : null;

    // Extract config values
    const AUTOPLAY = config.AUTOPLAY || false;
    let AUTOPLAY_INTERVAL_MS = config.AUTOPLAY_INTERVAL_MS || CONSTANTS.TIMING.DEFAULT_AUTOPLAY_INTERVAL;
    AUTOPLAY_INTERVAL_MS = AUTOPLAY_INTERVAL_MS < CONSTANTS.TIMING.MIN_AUTOPLAY_INTERVAL ? CONSTANTS.TIMING.MIN_AUTOPLAY_INTERVAL : AUTOPLAY_INTERVAL_MS;
    const BOARD_SIZE = config.BOARD_SIZE;
    const LEVEL_NAME = config.LEVEL_NAME;
    const LEVEL_ID = config.ID;
    const levelClass = `level-${LEVEL_ID}`;
    const BG_AUDIOS = config.BG_AUDIOS || [];
    const END_AUDIOS = config.END_AUDIOS || ["/mfersbeheaded/audio/shaya-beatbox-2.mp3"];
    const BG_IMAGE = config.BG_IMAGE || '';
    const ADV_SOUND_CLIPS = config.ADV_SOUND_CLIPS || [];
    const TIME_LIMIT = config.TIME_LIMIT;
    const MAX_MOVES = config.MAX_MOVES;
    const BARRELS_ENABLED = config.BARRELS_ENABLED || false;
    const BARREL_POSITIONS = config.BARREL_POSITIONS || [];
    const IMAGE_MAP = config.IMAGE_MAP;
    const POWER_UPS_ENABLED = config.POWER_UPS_ENABLED || false;
    const VERSION = config.VERSION || "1.0";
    const IS_TEST_LEVEL = config.IS_TEST || false;
    const BOARD_INITIAL_STATE = config.BOARD_INITIAL_STATE || null;

    const levelDetails: LevelDetails = {
        LEVEL_NAME: config.LEVEL_NAME,
        LEVEL_ID: LEVEL_ID,
        LEVEL_VERSION: config.VERSION || "1.0",
        CREDITS: config.CREDITS || [],
        TIME_LIMIT: TIME_LIMIT,
        MAX_MOVES: MAX_MOVES,
    };

    // UI state
    const [forceHideGameOverModal, setForceHideGameOverModal] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const userNameFromQueryParam = sanitizeUserName(searchParams.get('userName'));

    // Check for preset name: authenticated user's displayName > sessionStorage > query param > random
    const storedPlayerName = sessionStorage.getItem('mfersbeheaded_playerName');

    // Generate a random name if no preset name exists
    const generateRandomName = (): string => {
        const adjectives = ['Swift', 'Bold', 'Chill', 'Epic', 'Lucky', 'Brave'];
        const nouns = ['Mfer', 'Player', 'Gamer', 'Hero', 'Champion', 'Legend'];
        const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
        const noun = nouns[Math.floor(Math.random() * nouns.length)];
        const num = Math.floor(Math.random() * 999);
        return `${adj}${noun}${num}`;
    };

    const presetName = authDisplayName || storedPlayerName || userNameFromQueryParam || generateRandomName();

    // Never show modal - always auto-start
    const [isNameModalOpen, setNameModalOpen] = useState<boolean>(false);
    // Use localStorage with mfersbeheaded-specific keys (synced with Settings page)
    // Settings stores "enabled" (true = on), we store "muted" (true = off), so invert
    const [musicMuted, setIsMusicMuted] = useState<boolean>(() => localStorage.getItem('mfersbeheaded_music') === 'false');
    const [soundMuted, setIsSoundMuted] = useState<boolean>(() => localStorage.getItem('mfersbeheaded_sfx') === 'false');
    const [name, setName] = useState<string | null>(presetName || null);
    const [gamePlayUUID] = useState<string>(uuidv4());
    const [autoStartTriggered, setAutoStartTriggered] = useState<boolean>(false);
    const [hasReplaySession, setHasReplaySession] = useState<boolean>(false);
    const [replayResult, setReplayResult] = useState<any>(null);
    const [showCoordinates, setShowCoordinates] = useState<boolean>(false);

    // Refs to hold current values for keyboard handler (avoids stale closures)
    const boardRef = useRef<GameBoard>([]);
    const gameStatsRef = useRef<any>({ totalScore: 0, movesUsed: 0 });

    // Force update helper
    function useForceUpdate(): () => void {
        const [, setValue] = useState<number>(0);
        return () => setValue(value => value + 1);
    }
    const forceUpdate = useForceUpdate();

    // Initialize audio hook first (needed by other hooks)
    const { playSound, playWrongSound, playSuperPowerUpSound } = useGameAudio(config, soundMuted);

    // Advanced sound player
    function playAdvancedSoundNTimes(n: number): void {
        if (soundMuted) {
            return;
        }
        if (ADV_SOUND_CLIPS.length === 0) {
            return;
        }
        const randomIndex = Math.floor(Math.random() * ADV_SOUND_CLIPS.length);
        let snd = new Howl({
            src: [ADV_SOUND_CLIPS[randomIndex]],
            autoplay: false,
            loop: false,
            volume: 0.5,
        });
        snd.play();
    }

    // Initialize board state hook
    const boardStateHook = useBoardState(
        {
            BOARD_SIZE,
            BARRELS_ENABLED,
            BARREL_POSITIONS,
            POWER_UPS_ENABLED: POWER_UPS_ENABLED,
            BOARD_INITIAL_STATE,
        },
        forceUpdate
    );

    const {
        initializeBoard,
        placeBattlefieldBarrels,
        itemsToClear,
        isJewelToBeCleared,
        findSwapForMatch,
    } = boardStateHook;

    // Initialize board - useMemo to ensure it's only calculated once
    const [initBoard, initStateChanges] = useMemo(() => {
        return initializeBoard();
    }, [initializeBoard]);
    const [board] = useState<GameBoard>(initBoard);

    // Initialize game state hook
    const gameStateHook = useGameState(config, initStateChanges);
    const { gameState, endTime, gameStats, elapsedTimeSince, endGame } = gameStateHook;

    // Initialize replay recorder hook
    const replayHook = useReplayRecorder(
        gamePlayUUID,
        { LEVEL_ID, LEVEL_NAME, BOARD_SIZE },
        userId  // User ID from new auth system for score attribution
    );

    const {
        createReplaySession,
        finalizeReplay,
        recordGameState,
    } = replayHook;

    // Initialize jewel interaction hook
    // Cast replayHook to satisfy interface since useReplayRecorder is JS
    const { selectedJewel, handleJewelClicked } = useJewelInteraction({
        board,
        gameState,
        BOARD_SIZE,
        BARRELS_ENABLED,
        TIME_LIMIT,
        MAX_MOVES,
        boardStateHook,
        gameStateHook,
        replayHook: replayHook as Parameters<typeof useJewelInteraction>[0]['replayHook'],
        audioHook: { playSound, playWrongSound, playSuperPowerUpSound },
        playAdvancedSoundNTimes,
    });

    // Initialize autoplay hook
    useAutoplay(
        AUTOPLAY,
        AUTOPLAY_INTERVAL_MS,
        board,
        selectedJewel,
        gameState,
        findSwapForMatch,
        handleJewelClicked
    );

    // Initialize swipe interaction hook
    // Swipe triggers: click on from-tile, then click on to-tile
    const handleSwap = React.useCallback((fromRow: number, fromCol: number, toRow: number, toCol: number) => {
        // First click selects the "from" tile
        handleJewelClicked(fromRow, fromCol);
        // Second click triggers the swap to the "to" tile
        // Use setTimeout to ensure state updates between clicks
        setTimeout(() => {
            handleJewelClicked(toRow, toCol);
        }, 0);
    }, [handleJewelClicked]);

    const { containerRef: swipeContainerRef, handlers: swipeHandlers } = useSwipeInteraction({
        boardSize: BOARD_SIZE,
        enabled: !isNameModalOpen && gameState !== CONSTANTS.GAME_STATE_COMPLETE,
        onSwap: handleSwap
    });

    // Handle name confirmation
    const handleNameConfirmation = async (name: string): Promise<void> => {
        setName(name);

        // Capture CURRENT board state (after initial sweep) instead of initStateChanges
        // This ensures we record the actual board the player is seeing
        console.log('=== handleNameConfirmation - Capturing Board State ===');
        console.log('Full board dump before creating currentBoardChanges:');
        board.forEach((row, rowIndex) => {
            console.log(`Row ${rowIndex}:`, row.join(', '));
        });
        console.log('Specific check - board[2][1]:', board[2][1]);
        console.log('====================================================');

        const currentBoardChanges: [number, number, number][] = [];
        for (let row = 0; row < board.length; row++) {
            for (let col = 0; col < board[row].length; col++) {
                if (board[row][col] !== 0) {
                    currentBoardChanges.push([row, col, board[row][col]]);
                }
            }
        }

        // Always create replay session (private by default)
        const created = await createReplaySession(name, currentBoardChanges);
        setHasReplaySession(created);
        setNameModalOpen(false);
    };

    // Auto-start game immediately (no modal needed)
    useEffect(() => {
        if (!autoStartTriggered && !hasReplaySession) {
            // Clear sessionStorage so refresh generates a new name
            if (storedPlayerName) {
                sessionStorage.removeItem('mfersbeheaded_playerName');
            }
            setAutoStartTriggered(true);
            // Start game with the preset name
            handleNameConfirmation(presetName);
        }
    }, [autoStartTriggered, hasReplaySession, storedPlayerName, presetName]);

    // Save audio preferences to localStorage (synced with Settings page)
    // Settings stores "enabled" (true = on), we store "muted" (true = off), so invert
    useEffect(() => {
        localStorage.setItem('mfersbeheaded_music', (!musicMuted).toString());
    }, [musicMuted]);

    useEffect(() => {
        localStorage.setItem('mfersbeheaded_sfx', (!soundMuted).toString());
    }, [soundMuted]);

    // Send telemetry after name modal closes for PWA debugging
    useEffect(() => {
        if (!isNameModalOpen) {
            sendTelemetry('MfersBeheaded');
        }
    }, [isNameModalOpen]);

    // Update refs when board or gameStats change (for keyboard handler)
    useEffect(() => {
        boardRef.current = board;
        gameStatsRef.current = gameStats;
    }, [board, gameStats]);

    // Keyboard toggle for coordinate overlay (debug feature - dev/staging only)
    useEffect(() => {
        // Only enable debug key in development or staging environments
        const hostname = window.location.hostname;
        const isDevOrStaging = hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname.includes('staging');

        if (!isDevOrStaging) {
            return; // Don't register keyboard handler in production
        }

        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'c' || event.key === 'C') {
                // Dump board state to console (using refs for current values)
                console.log('=== LIVE BOARD STATE DUMP ===');
                console.log('Session ID:', gamePlayUUID);
                console.log('Board size:', boardRef.current.length, 'x', boardRef.current[0]?.length);
                console.log('Board state:');
                boardRef.current.forEach((row, rowIndex) => {
                    console.log(`Row ${rowIndex}:`, row.join(', '));
                });
                console.log('Score:', gameStatsRef.current.totalScore);
                console.log('Moves:', gameStatsRef.current.movesUsed);
                console.log('============================');

                setShowCoordinates(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [gamePlayUUID]); // Only depends on gamePlayUUID (never changes)

    // Note: We don't finalize replay on unmount because:
    // 1. If game ended properly, finalize was already called with correct game_state
    // 2. If user navigates away mid-game, this would send game_state: 0 and overwrite the real end state
    // The replay system handles incomplete games via the 24-hour expiration for private replays

    const navigateWithUserName = (): void => {
        // Navigate back to landing page to select level
        navigate('/mfersbeheaded/');
    };


    // Initial board setup - sweep and place barrels
    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            const { sweepBoard: sweepBoardFromHook, placeBattlefieldBarrels: placeBarrels } = boardStateHook;
            const { recordBarrelPlace } = replayHook;

            // Sweep board initially (not live play)
            await sweepBoardFromHook(
                board,
                playSound,
                playAdvancedSoundNTimes,
                false,
                elapsedTimeSince
            );

            // Place barrels after initial population and cleaning
            const barrelChanges = placeBarrels(board);

            // Record barrel placement
            if (barrelChanges.length > 0) {
                recordBarrelPlace(0, barrelChanges);
            }

            forceUpdate(); // Update the UI to show barrels
        };
        fetchData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        switch (LEVEL_ID) {
            case 'nakamigo_crush':
                document.title = 'nakamigo crush';
                break;
            default:
                document.title = 'mfers beheaded';
        }
    }, [LEVEL_ID]); // The effect will run every time levelID changes

    // Dynamic background styles for special levels
    const getLevelContainerStyle = (): React.CSSProperties => {
        if (LEVEL_ID === 'house_songs') {
            return {
                backgroundImage: 'url(/images/mfersbeheaded/house_songs_title.png_small)',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center top'
            };
        }
        return {};
    };

    const getGameContainerStyle = (): React.CSSProperties => {
        if (LEVEL_ID === 'house_songs') {
            return {
                backgroundImage: 'url(/images/mfersbeheaded/house_songs_background.png_small)',
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat'
            };
        }
        return {};
    };

    return (
        <div className={`gameWiderContainer ${levelClass}`} style={{backgroundImage: `url(${BG_IMAGE})`}}>
        <div className={`gameContainer ${levelClass}`} style={getGameContainerStyle()}>
            <div className="header">
                <div className={`levelContainer ${levelClass}`} style={getLevelContainerStyle()}>
                    {LEVEL_ID !== 'house_songs' && LEVEL_NAME}
                </div>
                <div className="statsContainer">
                    <PlayerInfo
                        anonName={name}
                    />
                    <div className={`scoreContainer ${levelClass}`}>
                        score: {gameStats.totalScore}
                    </div>
                    <Timer
                        endTime={endTime}
                        gameState={gameState}
                        timeLimit={TIME_LIMIT}
                        levelID={LEVEL_ID}
                        onTimeElapsed={async () => {
                            endGame();

                            // Record game over due to time
                            recordGameState(
                                elapsedTimeSince(),
                                CONSTANTS.GAME_STATE_COMPLETE,
                                gameStats.movesUsed,
                                elapsedTimeSince()
                            );

                            // Finalize replay and capture result (with final board state)
                            const result = await finalizeReplay({
                                final_score: gameStats.totalScore,
                                final_moves: gameStats.movesUsed,
                                game_state: CONSTANTS.GAME_STATE_COMPLETE,
                                duration_ms: elapsedTimeSince(),
                                final_board: board,
                            });
                            setReplayResult(result);
                        }}
                    />
                    <MovesCounter
                        movesUsed={gameStats.movesUsed}
                        maxMoves={MAX_MOVES}
                        levelID={LEVEL_ID}
                    />
                </div>
            </div>
            <div
                className={`board ${levelClass}`}
                data-testid="game-board"
                ref={swipeContainerRef}
                onTouchStart={swipeHandlers.onTouchStart}
                onTouchEnd={swipeHandlers.onTouchEnd}
            >
                {board.map((row, rowIndex) => (
                    <div className="row" key={rowIndex}>
                        {row.map((jewelType, colIndex) => {

                            const shouldBeCleared = isJewelToBeCleared(rowIndex, colIndex);

                            return ( <MferHead
                                key={colIndex}
                                type={jewelType}
                                x={rowIndex}
                                y={colIndex}
                                imageMap={IMAGE_MAP}
                                levelID={LEVEL_ID}
                                selectedJewel={selectedJewel}
                                shouldBeCleared={shouldBeCleared}
                                onClick={() => handleJewelClicked(rowIndex, colIndex)}
                                showCoordinates={showCoordinates}
                            />
                        )})}
                    </div>
                ))}
            </div>

            <div className={`buttonContainer ${levelClass}`}>
                {!location.pathname.includes('kids') && <button className={`back-to-home ${levelClass}`} onClick={() => navigate('/')}>← Back</button>}
                <button
                    className="mute-btn"
                    onClick={() => setIsMusicMuted(!musicMuted)}
                    title={musicMuted ? "Unmute Music" : "Mute Music"}
                >
                    <span className={musicMuted ? 'muted-icon' : ''}>🎵</span>
                </button>
                <button
                    className="mute-btn"
                    onClick={() => setIsSoundMuted(!soundMuted)}
                    title={soundMuted ? "Unmute Sound" : "Mute Sound"}
                >
                    <span className={soundMuted ? 'muted-icon' : ''}>🔊</span>
                </button>
                {gameState === CONSTANTS.GAME_STATE_COMPLETE ? (
                    <button className={`play-again ${levelClass}`} onClick={() => {navigateWithUserName();}}>play again</button>
                ) : (
                    <button className={`end-game ${levelClass}`} onClick={async () => {
                        endGame();

                        // Record game over and finalize
                        recordGameState(
                            elapsedTimeSince(),
                            CONSTANTS.GAME_STATE_COMPLETE,
                            gameStats.movesUsed,
                            elapsedTimeSince()
                        );

                        // Finalize replay and capture result (with final board state)
                        const result = await finalizeReplay({
                            final_score: gameStats.totalScore,
                            final_moves: gameStats.movesUsed,
                            game_state: CONSTANTS.GAME_STATE_COMPLETE,
                            duration_ms: elapsedTimeSince(),
                            final_board: board,
                        });
                        setReplayResult(result);
                    }}>End Game</button>
                )}
            </div>


            {/* Hidden state div for E2E testing */}
            <div
                id="game-state-data"
                data-testid="game-state"
                style={{ display: 'none' }}
                data-game-state={gameState}
                data-score={gameStats.totalScore}
                data-moves={gameStats.movesUsed}
                data-power-ups={gameStats.powerUps}
                data-selected-cell={selectedJewel ? `${selectedJewel.rowIndex}-${selectedJewel.colIndex}` : ''}
                data-board-state={board.map(row => row.join(',')).join(';')}
                data-level-id={LEVEL_ID}
                data-level-name={LEVEL_NAME}
            />


            <GameOverModal
                forceClosed={forceHideGameOverModal}
                gameState={gameState}
                gameStats={gameStats}
                name={name}
                autoplay={AUTOPLAY}
                levelDetails={levelDetails}
                isTestLevel={IS_TEST_LEVEL}
                levelID={LEVEL_ID}
                gamePlayUUID={gamePlayUUID}
                hasReplaySession={hasReplaySession}
                replayResult={replayResult}
                onClose={() => setForceHideGameOverModal(true)}
            />
            <BackgroundMusic
                audioRef={audioRef}
                gameState={gameState}
                musicMuted={musicMuted}
                audios={BG_AUDIOS}
                endAudios={END_AUDIOS}
            />

        </div>
        </div>
    );
}

export default Board;
